import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2 } from "lucide-react";

const empty = {
  name: "", business_id: "", email: "", phone: "",
  address: "", postal_code: "", city: "", country: "Suomi",
  contact_person: "", notes: "",
};

export default function CustomerForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/customers/${id}`).then((r) => setForm({ ...empty, ...r.data })).catch((e) => {
      toast.error(formatApiError(e));
      navigate("/crm");
    }).finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nimi on pakollinen"); return; }
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || null };
      const r = isEdit ? await api.put(`/customers/${id}`, payload) : await api.post("/customers", payload);
      toast.success(isEdit ? "Asiakas päivitetty" : "Asiakas luotu");
      navigate(`/crm/${r.data.id}`);
      if (!isEdit) setForm({ ...form, ...r.data });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Poistetaanko asiakas pysyvästi?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Asiakas poistettu"); navigate("/crm"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Ladataan...</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="customer-form-page">
      <header className="flex items-center gap-3">
        <Link to="/crm"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/70">{isEdit ? "Muokkaa asiakasta" : "Uusi asiakas"}</p>
          <h1 className="text-2xl font-display gold-text">{isEdit ? form.name : "Uusi asiakas"}</h1>
        </div>
        {isEdit && (
          <Button variant="outline" onClick={remove} data-testid="customer-delete-btn" className="border-destructive/40 text-destructive hover:bg-destructive/10">
            <Trash2 className="w-4 h-4 mr-2" /> Poista
          </Button>
        )}
        <Button onClick={save} disabled={saving} data-testid="customer-save-btn" className="bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Tallennetaan..." : "Tallenna"}
        </Button>
      </header>

      <Card className="panel-3d border-0">
        <CardHeader><CardTitle className="text-base font-display gold-text">Perustiedot</CardTitle></CardHeader>
        <CardContent className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nimi *" value={form.name} onChange={update("name")} testid="customer-name" />
          <Field label="Y-tunnus" value={form.business_id} onChange={update("business_id")} />
          <Field label="Sähköposti" value={form.email} onChange={update("email")} testid="customer-email" />
          <Field label="Puhelin" value={form.phone} onChange={update("phone")} />
          <Field label="Yhteyshenkilö" value={form.contact_person} onChange={update("contact_person")} />
        </CardContent>
      </Card>

      <Card className="panel-3d border-0">
        <CardHeader><CardTitle className="text-base font-display gold-text">Osoite</CardTitle></CardHeader>
        <CardContent className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Katuosoite" value={form.address} onChange={update("address")} full />
          <Field label="Postinumero" value={form.postal_code} onChange={update("postal_code")} />
          <Field label="Kaupunki" value={form.city} onChange={update("city")} />
          <Field label="Maa" value={form.country} onChange={update("country")} />
        </CardContent>
      </Card>

      <Card className="panel-3d border-0">
        <CardHeader><CardTitle className="text-base font-display gold-text">Muistiinpanot</CardTitle></CardHeader>
        <CardContent className="relative z-10">
          <Textarea value={form.notes || ""} onChange={update("notes")} rows={4} className="bg-input/40" />
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, full, testid }) {
  return (
    <div className={`${full ? "md:col-span-2" : ""} space-y-2`}>
      <Label>{label}</Label>
      <Input value={value || ""} onChange={onChange} data-testid={testid} className="bg-input/40" />
    </div>
  );
}
